import { Link } from 'react-router-dom';
import { useState } from 'react';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import type { JSX } from 'react';

const categoryFilters = ['全部', '平台推荐', '行业分析', '主播故事', '直播指南', '榜单排行'];

interface Article {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  cover: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  views: number;
  tags: string[];
}

export default function Articles(): JSX.Element {
  const [activeFilter, setActiveFilter] = useState('全部');
  const filteredArticles = activeFilter === '全部'
    ? articles
    : articles.filter((a: Article) => a.category === activeFilter);

  return (
    <>
      <PageMeta
        title="直播资讯 - 美女直播行业动态与观看指南"
        description="获取最新的美女直播行业资讯，包括平台推荐评测、主播故事、直播互动指南、行业分析等内容。了解直播行业最新动态。"
        keywords="美女直播,直播资讯,直播行业,主播故事,直播指南"
        canonicalUrl="https://live-platform.example.com/articles"
      />

      <div className="pt-20 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '直播资讯' },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              直播资讯
            </h1>
            <p className="text-foreground-600 text-base max-w-2xl">
              获取最新的直播行业动态、平台推荐评测和观看指南，助你更好地享受直播乐趣
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            {categoryFilters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeFilter === filter
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {filteredArticles.map((article: Article) => (
              <Link
                key={article.id}
                to={`/articles/${article.id}`}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={article.cover}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                      {article.category}
                    </span>
                    <span className="text-foreground-400 text-xs">{article.readTime}</span>
                  </div>
                  <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-foreground-400 text-xs">
                      <span>{article.author}</span>
                      <span>·</span>
                      <span>{article.date}</span>
                    </div>
                    <span className="flex items-center gap-1 text-foreground-400 text-xs">
                      <i className="ri-eye-line text-[10px]"></i>
                      {article.views.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}